import React, { useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import { formatCurrency } from '../../../../../shared/helpers/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  useStyles,
  AmountTypography,
  LabelTypography,
} from './amountCardsStyle';
import PropTypes from 'prop-types';
import getDefaultFundAllocations from '../../helpers/allocations';

export default function AmountCard({
  title,
  amount,
  custom,
  helptext,
  superscript,
  selected,
  investmentAmount,
  setInvestmentAmount,
  onClick,
  isReadonly,
  setFormData,
  onUpdateAllocation,
}) {
  const classes = useStyles();

  const onClickCard = (event) => {
    if (isReadonly) return;

    const isInputClick =
      event.target instanceof HTMLElement &&
      event.target.tagName.toLowerCase() === 'input';

    if (custom && isInputClick && selected) return;

    onClick();
  };

  const onBlurInvestmentAmount = useCallback(
    (event) => {
      const formattedValue = event.target.value;
      const numericValue = formattedValue.replace(/[$,]/g, '');
      setInvestmentAmount(numericValue ?? '');
      setFormData((prevData) => {
        const updatedFormData = {
          ...prevData,
          amount: numericValue,
          ...getDefaultFundAllocations(numericValue),
        };
        onUpdateAllocation(updatedFormData);
        return updatedFormData;
      });
    },
    [onUpdateAllocation, setFormData, setInvestmentAmount],
  );

  return (
    <div
      className={`${classes.card} ${selected ? 'selected' : ''}`}
      onClick={onClickCard}
    >
      <LabelTypography>
        {title} {superscript && <sup>{superscript}</sup>}
      </LabelTypography>
      {custom ? (
        <div
          className={`${classes.inputContainer} ${selected ? 'selected' : ''}`}
        >
          <NumericFormat
            className={classes.input}
            disabled={isReadonly}
            value={investmentAmount}
            prefix="$"
            type="text"
            thousandsGroupStyle="thousand"
            thousandSeparator=","
            placeholder={helptext || ''}
            maxLength={50}
            decimalScale={0}
            onBlur={onBlurInvestmentAmount}
            allowNegative={false}
          />
        </div>
      ) : (
        <div className={`${classes.value} ${selected ? 'selected' : ''}`}>
          <AmountTypography>{formatCurrency(amount)}</AmountTypography>
        </div>
      )}
      {selected && (
        <span
          className={classes.icon}
          style={{
            width: 18,
            height: 18,
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CheckCircleIcon />
        </span>
      )}
    </div>
  );
}

AmountCard.propTypes = {
  title: PropTypes.string.isRequired,
  amount: PropTypes.number,
  custom: PropTypes.bool,
  helptext: PropTypes.string,
  superscript: PropTypes.string,
  selected: PropTypes.bool,
  investmentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setInvestmentAmount: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  isReadonly: PropTypes.bool,
  setFormData: PropTypes.func.isRequired,
  onUpdateAllocation: PropTypes.func.isRequired,
};
